import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, inject, Injector, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalComponent,
  onyxTextareaLengthValidator,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxUploadComponent,
} from '@onyx/angular';
import { map, of, Subject, switchMap } from 'rxjs';
import { ValidationHelper } from '../../../../helpers/validation.helper';
import type { BatchFileUpload } from '../../../../interfaces/utilities/batch-file-upload';
import { StorageService } from '../../../../services/storage.service';

export type DocumentModalData =
  | { mode: OnyxFormMode.ADD }
  | { mode: OnyxFormMode.EDIT; document: DocumentForm };
export type DocumentModalResult = DocumentForm | null | undefined;

export type DocumentFormGroup = ReturnType<
  typeof DocumentsModalFormComponent.buildForm
>;
export type DocumentForm = ReturnType<DocumentFormGroup['getRawValue']>;

@Component({
  selector: 'app-documents-modal-form',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxUploadComponent,
    OnyxButtonComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './documents-modal-form.component.html',
  styleUrl: './documents-modal-form.component.scss',
})
export class DocumentsModalFormComponent {
  protected readonly data = inject<DocumentModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<DocumentModalResult>);
  private readonly storageService = inject(StorageService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'forms.documents';

  protected readonly OnyxFormMode = OnyxFormMode;

  protected form: DocumentFormGroup;
  protected loading = signal(false);
  protected close$ = new Subject<DocumentModalResult>();

  constructor() {
    this.form = DocumentsModalFormComponent.buildForm(this.fb);
    if (this.data.mode === OnyxFormMode.EDIT) {
      this.form.setValue(this.data.document);
    }
  }

  protected remove(): void {
    this.close$.next(null);
  }

  protected cancel(): void {
    this.close$.next(undefined);
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const dto = this.form.getRawValue();

    this.loading.set(true);
    of(dto)
      .pipe(
        map((dto): BatchFileUpload[] => [
          {
            path: 'attachments',
            files: dto.attachments as any,
          },
        ]),
        switchMap((data) => this.storageService.uploadBatch(data)),
        map((data) => this.storageService.mergeBatch(dto, data)),
      )
      .subscribe((data) => this.close$.next(data))
      .add(() => this.loading.set(false));
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      name: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      note: fb.control<string | null>(null, [onyxTextareaLengthValidator]),
      attachments: fb.control<string[]>([], [Validators.required]),
    });
  }
}
